import React, { useRef } from 'react';
import { Download, Building2, Video, TrendingUp, Users, CheckCircle, Mail, Phone, Globe } from 'lucide-react';

export default function CorporateProposal({ companyName = '[Company Name]', contactName = '[Contact Name]' }) {
  const proposalRef = useRef(null);

  const handleDownload = () => {
    // Create printable version
    const printContent = proposalRef.current.innerHTML;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>BrandStage Corporate Proposal - ${companyName}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { 
              font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; 
              color: #1a1a1a; 
              line-height: 1.6;
              padding: 40px;
              max-width: 800px;
              margin: 0 auto;
            }
            .header { text-align: center; margin-bottom: 40px; padding-bottom: 20px; border-bottom: 3px solid #C41E3A; }
            .logo { font-size: 28px; font-weight: 900; letter-spacing: -1px; }
            .logo span { color: #C41E3A; }
            .tagline { color: #666; font-size: 14px; margin-top: 5px; }
            h1 { font-size: 24px; color: #1a1a1a; margin: 30px 0 15px; }
            h2 { font-size: 18px; color: #C41E3A; margin: 25px 0 10px; }
            p { margin-bottom: 15px; color: #333; }
            .highlight { background: #fff5f5; border-left: 4px solid #C41E3A; padding: 15px 20px; margin: 20px 0; }
            .services { display: grid; grid-template-columns: 1fr 1fr; gap: 15px; margin: 20px 0; }
            .service { background: #f8f8f8; padding: 15px; border-radius: 8px; }
            .service-title { font-weight: 600; color: #1a1a1a; margin-bottom: 5px; }
            .service-desc { font-size: 13px; color: #666; }
            .ecosystem { background: #f0f0f0; padding: 20px; border-radius: 8px; margin: 20px 0; }
            .ecosystem-brands { display: flex; justify-content: space-between; margin-top: 15px; font-size: 13px; }
            .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #ddd; text-align: center; font-size: 13px; color: #666; }
            .contact-info { margin-top: 15px; }
            .contact-info a { color: #C41E3A; text-decoration: none; }
            @media print { body { padding: 20px; } }
          </style>
        </head>
        <body>
          ${printContent}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Download Button */}
      <div className="flex justify-end mb-6">
        <button
          onClick={handleDownload}
          className="flex items-center gap-2 bg-[#C41E3A] hover:bg-[#A01830] text-white px-6 py-3 rounded-lg font-medium transition-colors"
          data-testid="download-proposal-btn"
        >
          <Download className="w-5 h-5" />
          Download Proposal
        </button>
      </div>

      {/* Proposal Content */}
      <div 
        ref={proposalRef}
        className="bg-white text-zinc-900 rounded-2xl p-8 md:p-12 shadow-xl"
        data-testid="corporate-proposal"
      >
        {/* Header */}
        <div className="header text-center mb-10 pb-6 border-b-4 border-[#C41E3A]">
          <div className="logo text-3xl font-black tracking-tight">
            Brand<span className="text-[#C41E3A]">Stage</span>
          </div>
          <p className="tagline text-zinc-500 mt-2">Video-First Leadership Narratives & Digital Positioning</p>
        </div>

        {/* Welcome */}
        <div className="mb-8">
          <p className="text-lg">Dear {contactName},</p>
          <p className="mt-4 text-zinc-700">
            Welcome to <strong>BrandStage</strong>. We're excited to partner with {companyName} in building a compelling digital presence that authentically represents your leadership voice and company vision.
          </p>
        </div>

        {/* What is BrandStage */}
        <h2 className="text-xl font-bold text-[#C41E3A] mt-8 mb-4">What is BrandStage?</h2>
        <p className="text-zinc-700">
          BrandStage is a video-first platform designed to help businesses tell their authentic story. We believe that trust is built through genuine human connection—and nothing conveys authenticity better than seeing and hearing real leaders speak about their vision, values, and purpose.
        </p>
        
        <div className="highlight bg-red-50 border-l-4 border-[#C41E3A] p-5 my-6">
          <p className="text-zinc-800 font-medium">
            "Video-first profiles increase your digital value. Better value improves engagement. Greater engagement drives influence and lead development."
          </p>
        </div>

        {/* What's Included */}
        <h2 className="text-xl font-bold text-[#C41E3A] mt-8 mb-4">Your BrandStage Package Includes</h2>
        
        <div className="grid md:grid-cols-2 gap-4 my-6">
          <div className="bg-zinc-100 p-5 rounded-xl">
            <div className="flex items-center gap-3 mb-2">
              <Building2 className="w-5 h-5 text-[#C41E3A]" />
              <span className="font-semibold">Company Profile</span>
            </div>
            <p className="text-sm text-zinc-600">A professionally crafted, shareable company profile with your story, leadership team, and multimedia content.</p>
          </div>
          
          <div className="bg-zinc-100 p-5 rounded-xl">
            <div className="flex items-center gap-3 mb-2">
              <Video className="w-5 h-5 text-[#C41E3A]" />
              <span className="font-semibold">Leadership Videos</span>
            </div>
            <p className="text-sm text-zinc-600">Professionally produced leadership interviews and video narratives that capture your authentic voice.</p>
          </div>
          
          <div className="bg-zinc-100 p-5 rounded-xl">
            <div className="flex items-center gap-3 mb-2">
              <TrendingUp className="w-5 h-5 text-[#C41E3A]" />
              <span className="font-semibold">Digital Readiness Score</span>
            </div>
            <p className="text-sm text-zinc-600">Benchmark your digital presence against industry standards and track improvements over time.</p>
          </div>
          
          <div className="bg-zinc-100 p-5 rounded-xl">
            <div className="flex items-center gap-3 mb-2">
              <Users className="w-5 h-5 text-[#C41E3A]" />
              <span className="font-semibold">BrandStage Connect</span>
              <span className="ml-1 text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-medium">Coming Soon</span>
            </div>
            <p className="text-sm text-zinc-600">Access to our curated network for discovering and responding to business opportunities. Launching soon.</p>
          </div>
        </div>

        {/* How We Work Together */}
        <h2 className="text-xl font-bold text-[#C41E3A] mt-8 mb-4">How We Work Together</h2>
        <p className="text-zinc-700 mb-4">
          As your digital positioning partner, we take a comprehensive approach:
        </p>
        
        <div className="space-y-3 mb-6">
          {[
            'Profile creation and ongoing management',
            'Video content strategy and production coordination',
            'Digital readiness assessment and improvement recommendations',
            'Network visibility through BrandStage Connect (coming soon)',
            'Regular performance insights and analytics'
          ].map((item, index) => (
            <div key={index} className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <span className="text-zinc-700">{item}</span>
            </div>
          ))}
        </div>

        {/* The Continex Ecosystem */}
        <h2 className="text-xl font-bold text-[#C41E3A] mt-8 mb-4">The Continex Ecosystem</h2>
        <div className="bg-zinc-100 p-6 rounded-xl my-6">
          <p className="text-zinc-700 mb-4">
            BrandStage is part of the <strong>Continex</strong> ecosystem—bringing nearly two decades of experience in cross-border trade, investment promotion, and business development to your digital presence.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 text-center text-sm">
            <div>
              <p className="font-bold text-zinc-800">Continex</p>
              <p className="text-zinc-500 text-xs">Cross-border trade & government engagement</p>
            </div>
            <div>
              <p className="font-bold text-zinc-800">BrandStage</p>
              <p className="text-zinc-500 text-xs">Digital positioning & video profiles</p>
            </div>
            <div>
              <p className="font-bold text-zinc-800">Cinimage</p>
              <p className="text-zinc-500 text-xs">Corporate films & content production</p>
            </div>
            <div>
              <p className="font-bold text-zinc-800">TradeVesta</p>
              <p className="text-zinc-500 text-xs">Trade missions & B2B platform</p>
            </div>
          </div>
        </div>

        {/* Annual Retainer */}
        <h2 className="text-xl font-bold text-[#C41E3A] mt-8 mb-4">Corporate Annual Retainer</h2>
        <p className="text-zinc-700">
          For corporate clients, we offer a customised annual retainer model that provides comprehensive digital positioning support tailored to your specific requirements. This includes dedicated account management, priority content production, and strategic guidance throughout the year.
        </p>
        <p className="text-zinc-700 mt-4">
          <strong>Your dedicated team will work with you to define the scope and deliverables based on your business objectives.</strong>
        </p>

        {/* Next Steps */}
        <h2 className="text-xl font-bold text-[#C41E3A] mt-8 mb-4">Next Steps</h2>
        <div className="space-y-3 mb-6">
          {[
            'Review and access your BrandStage profile dashboard',
            'Schedule an onboarding call with your account manager',
            'Share your brand guidelines and key messaging',
            'Plan your first video content session'
          ].map((item, index) => (
            <div key={index} className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-[#C41E3A] text-white flex items-center justify-center text-sm font-bold flex-shrink-0">
                {index + 1}
              </span>
              <span className="text-zinc-700">{item}</span>
            </div>
          ))}
        </div>

        {/* Closing */}
        <div className="mt-10 pt-6 border-t border-zinc-200">
          <p className="text-zinc-700">
            We're committed to helping {companyName} build a digital presence that truly reflects your leadership and opens doors to new opportunities.
          </p>
          <p className="text-zinc-700 mt-4">
            Welcome aboard.
          </p>
          <p className="mt-6 font-semibold text-zinc-800">
            The BrandStage Team
          </p>
        </div>

        {/* Footer */}
        <div className="footer mt-10 pt-6 border-t border-zinc-200 text-center text-sm text-zinc-500">
          <p className="font-semibold text-zinc-700">BrandStage</p>
          <p>A Continex Initiative</p>
          <div className="contact-info flex flex-wrap justify-center gap-4 mt-4">
            <a href="https://www.brandstage-media.com" className="flex items-center gap-1 text-[#C41E3A] hover:underline">
              <Globe className="w-4 h-4" /> brandstage-media.com
            </a>
            <a href="mailto:contact@brandstage-media.com" className="flex items-center gap-1 text-[#C41E3A] hover:underline">
              <Mail className="w-4 h-4" /> contact@brandstage-media.com
            </a>
          </div>
          <p className="mt-4 text-xs text-zinc-400">
            © {new Date().getFullYear()} BrandStage. Part of the Continex Ecosystem.
          </p>
        </div>
      </div>
    </div>
  );
}
